import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

export interface Achievement {
  id: string
  slug: string          // 所属游戏
  title: string
  description: string
  emoji: string
  unlockedAt: string | null
}

const DEFINED: Achievement[] = [
  // 全局
  { id: 'first_play',     slug: '*',       title: '起步',          description: '第一次游玩 Retro Arcade 的游戏',     emoji: '🎮', unlockedAt: null },
  { id: 'all_games',      slug: '*',       title: '全能玩家',      description: '每款游戏都尝试过至少一次',           emoji: '🏆', unlockedAt: null },
  { id: 'rank_top10',     slug: '*',       title: '新星玩家',      description: '在任意一款游戏拿到排行榜前 10',     emoji: '🌟', unlockedAt: null },

  // 贪吃蛇
  { id: 'snake_novice',   slug: 'snake',   title: '小蛇初长成',    description: '在贪吃蛇中拿到 50 分',               emoji: '🐣', unlockedAt: null },
  { id: 'snake_skilled',  slug: 'snake',   title: '灵蛇之舞',      description: '在贪吃蛇中拿到 150 分',              emoji: '🐍', unlockedAt: null },
  { id: 'snake_master',   slug: 'snake',   title: '蛇王',          description: '在贪吃蛇中拿到 300 分',              emoji: '👑', unlockedAt: null },

  // Tetris
  { id: 'tetris_line',    slug: 'tetris',  title: '初识消除',      description: '在 Tetris 中消除一行',               emoji: '🟦', unlockedAt: null },
  { id: 'tetris_tspin',   slug: 'tetris',  title: '连消达人',      description: '在 Tetris 中同时消除 4 行(Tetris)',   emoji: '💎', unlockedAt: null },
  { id: 'tetris_1k',      slug: 'tetris',  title: '千分玩家',      description: '在 Tetris 中拿到 1000 分',            emoji: '🎯', unlockedAt: null },

  // Flappy
  { id: 'flappy_first',   slug: 'flappy-bird', title: '初飞',      description: 'Flappy Bird 首次飞行',                emoji: '🐤', unlockedAt: null },
  { id: 'flappy_10',      slug: 'flappy-bird', title: '百公里',    description: 'Flappy Bird 拿到 10 分',              emoji: '✈️', unlockedAt: null },
  { id: 'flappy_50',      slug: 'flappy-bird', title: '翱翔天际',  description: 'Flappy Bird 拿到 50 分',              emoji: '🚀', unlockedAt: null },

  // 2048
  { id: '2048_128',       slug: '2048',    title: '小有所成',      description: '合成 128',                          emoji: '🧮', unlockedAt: null },
  { id: '2048_512',       slug: '2048',    title: '策略家',        description: '合成 512',                          emoji: '📐', unlockedAt: null },
  { id: '2048_2048',      slug: '2048',    title: '2048 达成',     description: '合成 2048,胜利!',                    emoji: '🏅', unlockedAt: null },

  // Minesweeper
  { id: 'mine_clear_easy',  slug: 'minesweeper', title: '排雷新手', description: '在简单难度获胜',                     emoji: '🚩', unlockedAt: null },
  { id: 'mine_clear_medium',slug: 'minesweeper', title: '排雷能手', description: '在中等难度获胜',                     emoji: '💣', unlockedAt: null },
  { id: 'mine_clear_hard',  slug: 'minesweeper', title: '排雷大师', description: '在困难难度获胜',                     emoji: '⭐', unlockedAt: null },
]

const STORAGE_KEY = 'ra_achievements'

function load(): Achievement[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFINED.map(a => ({ ...a }))
    const saved = JSON.parse(raw) as Record<string, string>
    return DEFINED.map(a => ({ ...a, unlockedAt: saved[a.id] || a.unlockedAt }))
  } catch {
    return DEFINED.map(a => ({ ...a }))
  }
}

export const useAchievementsStore = defineStore('achievements', () => {
  const achievements = ref<Achievement[]>(load())
  const playedSlugs = ref<Set<string>>(new Set(JSON.parse(localStorage.getItem('ra_played_slugs') || '[]')))
  const newlyUnlocked = ref<Achievement | null>(null)

  watch(
    achievements,
    val => {
      const map: Record<string, string> = {}
      for (const a of val) if (a.unlockedAt) map[a.id] = a.unlockedAt
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
    },
    { deep: true },
  )

  watch(playedSlugs, val => {
    localStorage.setItem('ra_played_slugs', JSON.stringify([...val]))
  }, { deep: true })

  const unlockedCount = computed(() => achievements.value.filter(a => a.unlockedAt).length)
  const total = computed(() => achievements.value.length)

  function unlock(id: string) {
    const a = achievements.value.find(x => x.id === id)
    if (!a || a.unlockedAt) return
    a.unlockedAt = new Date().toISOString()
    newlyUnlocked.value = a
  }

  function recordGame(slug: string) {
    playedSlugs.value.add(slug)
    void unlock('first_play')
    // 检查 all_games(总游戏数 9, 9 = released 4 + 5 coming, 检查 released 4 全玩过)
    const allReleasedSlugs = ['hello-cube', 'snake', 'tetris', 'flappy-bird', '2048', 'minesweeper']
    if (allReleasedSlugs.every(s => playedSlugs.value.has(s))) unlock('all_games')
  }

  function checkScore(slug: string, score: number) {
    if (slug === 'snake') {
      if (score >= 50) unlock('snake_novice')
      if (score >= 150) unlock('snake_skilled')
      if (score >= 300) unlock('snake_master')
    } else if (slug === 'tetris') {
      unlock('tetris_line')
      if (score >= 1000) unlock('tetris_1k')
    } else if (slug === 'flappy-bird') {
      unlock('flappy_first')
      if (score >= 10) unlock('flappy_10')
      if (score >= 50) unlock('flappy_50')
    } else if (slug === '2048') {
      if (score >= 128) unlock('2048_128')
      if (score >= 512) unlock('2048_512')
    }
  }

  function dismissNotification() { newlyUnlocked.value = null }

  return { achievements, playedSlugs, newlyUnlocked, unlockedCount, total, unlock, recordGame, checkScore, dismissNotification }
})
